import { useCallback, useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarX,
  Clock,
  Inbox,
  UserX,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  AppointmentsSplitPane,
  isSplitPaneWide,
  type SplitPaneAppointment,
  type SplitPaneTab,
} from "@/components/account/appointments/appointments-split-pane";
import { CancelReasonForm } from "@/components/account/appointments/cancel-reason-form";
import { RescheduleModal } from "@/components/account/appointments/reschedule-modal";
import {
  providerBookingRequestsQueryOptions,
  ProviderRequestsPanel,
  type ProviderRequestsView,
} from "@/components/account/booking-requests/provider-requests-panel";
import { PageHeader } from "@/components/layout/page-header";
import { LoadErrorState } from "@/components/ui/load-error-state";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { apiErrorCode, isBadStatusError, useApiErrorMessage } from "@/lib/api-error-message";
import { showToast } from "@/lib/ui/toast";

type Tab = SplitPaneTab;
const VALID_TABS: readonly Tab[] = [
  "requests",
  "pending",
  "upcoming",
  "past",
  "cancelled",
  "noshow",
] as const;
const VALID_REQUEST_VIEWS: readonly ProviderRequestsView[] = ["pending", "history"] as const;

interface Counts {
  pending: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  declined: number;
  noshow: number;
}

interface AppointmentsResponse {
  appointments: SplitPaneAppointment[];
  counts: Counts;
}

interface FacilityEntry {
  facilityId: string;
  facility: {
    id: string;
    name: unknown;
    addressCity: string | null;
  };
}

interface AppointmentsSearch {
  tab: Tab;
  facilityId?: string;
  type?: string;
  /** Sub-view of the `requests` tab — pending vs. history. */
  rq?: ProviderRequestsView;
}

const providerFacilitiesQueryOptions = {
  queryKey: ["api", "/api/account/provider/facilities"] as const,
  queryFn: () => apiFetch.get<{ facilities: FacilityEntry[] }>("/api/account/provider/facilities"),
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
    return failureCount < 2;
  },
};

export const Route = createFileRoute("/_authenticated/account/provider/appointments")({
  validateSearch: (search: Record<string, unknown>): AppointmentsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    const facilityId =
      typeof search.facilityId === "string" && search.facilityId ? search.facilityId : undefined;
    const type = typeof search.type === "string" && search.type ? search.type : undefined;
    const rawRq = typeof search.rq === "string" ? search.rq : "";
    return {
      tab: VALID_TABS.includes(raw as Tab) ? (raw as Tab) : "pending",
      facilityId,
      type,
      rq: VALID_REQUEST_VIEWS.includes(rawRq as ProviderRequestsView)
        ? (rawRq as ProviderRequestsView)
        : undefined,
    };
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(providerFacilitiesQueryOptions),
  component: ProviderAppointmentsPage,
});

function ProviderAppointmentsPage() {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const search = Route.useSearch();
  const navigate = useNavigate();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  // Counts come back with every panel fetch (API returns the full status
  // breakdown regardless of the requested tab) — the active panel reports
  // them up so the TabBar badges match legacy (provider-appointments-client
  // lines 236-264: pending / noshow / cancelled+declined).
  const [counts, setCounts] = useState<Counts | null>(null);

  const facilitiesQuery = useQuery(providerFacilitiesQueryOptions);

  // Dedicated count for the `requests` tab badge — shares its key with the
  // panel's own query, so one fetch serves both.
  const pendingRequestsQuery = useQuery(providerBookingRequestsQueryOptions("pending"));
  const pendingRequestCount = pendingRequestsQuery.data?.counts.pending ?? 0;

  const setRequestsView = useCallback(
    (rq: ProviderRequestsView) =>
      navigate({
        to: "/account/provider/appointments",
        search: { ...search, tab: "requests" as const, rq },
        resetScroll: false,
      }),
    [navigate, search],
  );

  const setTab = useCallback(
    (tab: Tab) =>
      navigate({
        to: "/account/provider/appointments",
        search: { ...search, tab },
      }),
    [navigate, search],
  );

  const setFilter = (key: "facilityId" | "type", value: string) =>
    navigate({
      to: "/account/provider/appointments",
      search: { ...search, [key]: value || undefined },
    });

  const facilityOptions = (facilitiesQuery.data?.facilities ?? []).map((f) => ({
    id: f.facilityId,
    name: pickFacilityName(f.facility.name, locale),
    city: f.facility.addressCity ?? "",
  }));

  // Legacy badge mapping: pending → counts.pending, noshow → counts.noshow,
  // cancelled → cancelled + declined; upcoming/past have no badge.
  const countFor = (id: Tab): number | null => {
    if (id === "requests") return pendingRequestCount;
    if (!counts) return null;
    if (id === "pending") return counts.pending;
    if (id === "cancelled") return counts.cancelled + counts.declined;
    if (id === "noshow") return counts.noshow;
    return null;
  };

  const tabs: TabItem<Tab>[] = (
    [
      { id: "requests" as const, label: t`Requests`, icon: <Inbox className="size-4" /> },
      { id: "pending" as const, label: t`Pending`, icon: <Clock className="size-4" /> },
      { id: "upcoming" as const, label: t`Upcoming`, icon: <CalendarClock className="size-4" /> },
      { id: "past" as const, label: t`Past`, icon: <CalendarCheck className="size-4" /> },
      { id: "cancelled" as const, label: t`Cancelled`, icon: <CalendarX className="size-4" /> },
      { id: "noshow" as const, label: t`No-show`, icon: <UserX className="size-4" /> },
    ] satisfies TabItem<Tab>[]
  ).map((tab): TabItem<Tab> => {
    const count = countFor(tab.id);
    return {
      ...tab,
      trailing:
        count != null && count > 0 ? (
          <span
            className={cn(
              "min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[0.625rem] font-semibold",
              search.tab === tab.id
                ? "bg-primary/15 text-primary"
                : "bg-muted text-muted-foreground",
            )}
          >
            {count}
          </span>
        ) : undefined,
    };
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        className="flex-nowrap"
        title={<Trans>Appointments</Trans>}
        description={<Trans>Pending requests, upcoming visits, and history.</Trans>}
        actions={
          <Link
            to="/account/provider/calendar"
            className="border-border hover:bg-muted inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium"
            aria-label={t`Open calendar`}
          >
            <CalendarDays className="size-4" aria-hidden />
            <span className="hidden sm:inline">
              <Trans>Open calendar</Trans>
            </span>
          </Link>
        }
      />

      <TabBar
        tabs={tabs}
        activeTab={search.tab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
      />

      {search.tab !== "requests" && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={search.facilityId ?? ""}
            onChange={(e) => setFilter("facilityId", e.target.value)}
            className="bg-card border-border text-foreground h-10 rounded-lg border px-3 text-sm"
            aria-label={t`Filter by facility`}
          >
            <option value="">{t`All facilities`}</option>
            {facilityOptions.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name || f.city}
              </option>
            ))}
          </select>
          <select
            value={search.type ?? ""}
            onChange={(e) => setFilter("type", e.target.value)}
            className="bg-card border-border text-foreground h-10 rounded-lg border px-3 text-sm"
            aria-label={t`Filter by type`}
          >
            <option value="">{t`All types`}</option>
            <option value="consultation">{t`Consultation`}</option>
            <option value="control">{t`Follow-up`}</option>
            <option value="procedure">{t`Procedure`}</option>
            <option value="video">{t`Video`}</option>
            <option value="home_visit">{t`Home visit`}</option>
          </select>
        </div>
      )}

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={search.tab}
        tabIds={VALID_TABS}
        onTabChange={setTab}
        renderPanel={(panelKey) => (
          <div className="space-y-6">
            {panelKey === "requests" ? (
              <ProviderRequestsPanel
                view={search.rq ?? "pending"}
                onViewChange={setRequestsView}
                facilities={facilityOptions.map((f) => ({
                  id: f.id,
                  name: f.name || f.city,
                }))}
                locale={locale}
              />
            ) : (
              <AppointmentsPanel
                tab={panelKey}
                facilityId={search.facilityId}
                type={search.type}
                locale={locale}
                onCounts={setCounts}
              />
            )}
            <AccountSecurityBanner />
          </div>
        )}
      />
    </div>
  );
}

function AppointmentsPanel({
  tab,
  facilityId,
  type,
  locale,
  onCounts,
}: {
  tab: Tab;
  facilityId?: string;
  type?: string;
  locale: string;
  onCounts?: (counts: Counts) => void;
}) {
  const { t } = useLingui();
  const errorMessage = useApiErrorMessage();
  const [data, setData] = useState<AppointmentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [selected, setSelected] = useState<SplitPaneAppointment | null>(null);
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Modal targets — null when modal closed.
  const [reasonTarget, setReasonTarget] = useState<{
    appt: SplitPaneAppointment;
    variant: "decline" | "noshow";
  } | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<SplitPaneAppointment | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setActionError(null);
    try {
      const params = new URLSearchParams({ tab });
      if (facilityId) params.set("facilityId", facilityId);
      if (type) params.set("type", type);
      const res = await apiFetch.get<AppointmentsResponse>(
        `/api/account/provider/appointments?${params.toString()}`,
      );
      setData(res);
      setErrored(false);
      onCounts?.(res.counts);
      // Keep selection if still in list. From lg the first item fills the
      // detail pane (legacy behavior); below lg nothing is pre-selected, since
      // a selection opens the detail sheet (P21).
      setSelected((prev) => {
        if (prev && res.appointments.some((a) => a.id === prev.id)) return prev;
        return isSplitPaneWide() ? (res.appointments[0] ?? null) : null;
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const body = err.body as { error?: string } | null;
        if (body?.error === "TOTP_CHALLENGE_REQUIRED") {
          setErrored(true);
          setLoading(false);
          return;
        }
      }
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, [tab, facilityId, type, onCounts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runAction = async (
    id: string,
    path: "accept" | "decline" | "mark-noshow",
    body?: object,
  ) => {
    setActionPending(id + ":" + (path === "mark-noshow" ? "noshow" : path));
    setActionError(null);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.post(`/api/appointments/${id}/${path}`, body ?? {});
      await refresh();
      toast.update(t`Saved`, "success");
    } catch (err) {
      const msg = errorMessage(err, t`Action failed`);
      setActionError(msg);
      toast.update(msg, "error");
      // Someone else handled it first (patient cancelled or moved it,
      // colleague accepted): reload so the list matches the server.
      if (isBadStatusError(err) || apiErrorCode(err) === "CONFLICT") void refresh();
    } finally {
      setActionPending(null);
    }
  };

  const handleAccept = (id: string) => runAction(id, "accept");

  const openDecline = (appt: SplitPaneAppointment) => setReasonTarget({ appt, variant: "decline" });

  const openNoShow = (appt: SplitPaneAppointment) => setReasonTarget({ appt, variant: "noshow" });

  const handleReasonSubmit = async (reason: string | undefined) => {
    if (!reasonTarget) return;
    const { appt, variant } = reasonTarget;
    if (variant === "decline") {
      await runAction(appt.id, "decline", { reason });
    } else {
      await runAction(appt.id, "mark-noshow", { note: reason });
    }
    setReasonTarget(null);
  };

  const openReschedule = (appt: SplitPaneAppointment) => setRescheduleTarget(appt);

  const submitReschedule = async (newStartIso: string) => {
    if (!rescheduleTarget) return;
    const start = new Date(newStartIso);
    if (isNaN(start.getTime())) {
      setActionError(t`Invalid time`);
      return;
    }
    const end = new Date(start.getTime() + rescheduleTarget.duration * 60 * 1000);
    setActionPending(rescheduleTarget.id + ":reschedule");
    setActionError(null);
    const toast = showToast(t`Saving…`, "loading");
    try {
      await apiFetch.post("/api/account/provider/calendar/reschedule", {
        appointmentId: rescheduleTarget.id,
        newStartsAt: start.toISOString(),
        newEndsAt: end.toISOString(),
      });
      setRescheduleTarget(null);
      await refresh();
      toast.update(t`Saved`, "success");
    } catch (err) {
      const msg = errorMessage(err, t`Could not reschedule`);
      setActionError(msg);
      toast.update(msg, "error");
    } finally {
      setActionPending(null);
    }
  };

  if (errored)
    return (
      <LoadErrorState
        className="mt-2"
        title={<Trans>Could not load appointments.</Trans>}
        onRetry={() => void refresh()}
        retrying={loading}
      />
    );

  // Below lg the detail opens as a bottom sheet; the reason/reschedule
  // sheets then stack on top of it and must not pop its history sentinel.
  const detailSheetOpen = !!selected && !isSplitPaneWide();

  return (
    <div className="space-y-3 pb-6">
      {actionError && (
        <div className="bg-destructive/10 text-destructive rounded-lg px-3 py-2 text-xs">
          {actionError}
        </div>
      )}

      <AppointmentsSplitPane
        tab={tab}
        items={data?.appointments ?? []}
        loading={loading}
        locale={locale}
        selected={selected}
        onSelect={setSelected}
        actionPending={actionPending}
        onAccept={handleAccept}
        onDecline={openDecline}
        onNoShow={openNoShow}
        onReschedule={openReschedule}
      />

      <CancelReasonForm
        open={!!reasonTarget}
        patientName={reasonTarget ? patientNameOf(reasonTarget.appt, t`Unknown patient`) : ""}
        variant={reasonTarget?.variant ?? "decline"}
        pending={
          (reasonTarget &&
            actionPending ===
              reasonTarget.appt.id +
                ":" +
                (reasonTarget.variant === "decline" ? "decline" : "noshow")) ??
          false
        }
        onSubmit={handleReasonSubmit}
        onClose={() => setReasonTarget(null)}
        disableHistory={detailSheetOpen}
      />

      <RescheduleModal
        open={!!rescheduleTarget}
        appointment={
          rescheduleTarget
            ? { startsAt: rescheduleTarget.startsAt, duration: rescheduleTarget.duration }
            : null
        }
        pending={rescheduleTarget !== null && actionPending === rescheduleTarget.id + ":reschedule"}
        onSubmit={submitReschedule}
        onClose={() => setRescheduleTarget(null)}
        disableHistory={detailSheetOpen}
      />
    </div>
  );
}

// Fallback label is passed in (t`Unknown patient`) so this stays a pure
// module-level helper — mirrors legacy `t("unknownPatient")` fallback.
function patientNameOf(appt: SplitPaneAppointment, unknownLabel: string): string {
  return appt.profile ? `${appt.profile.firstName} ${appt.profile.lastName}` : unknownLabel;
}

function pickFacilityName(name: unknown, locale: string): string {
  if (!name) return "";
  if (typeof name === "object") {
    const obj = name as Record<string, string>;
    return obj[locale] ?? obj.en ?? obj.ro ?? Object.values(obj)[0] ?? "";
  }
  return String(name);
}
